import React, { useEffect, useId, useRef, useState } from "react"
import styles from "./ResizableSidebar.module.css"

const STORAGE_KEY = "turk.detailsSidebarWidth"
const DEFAULT_WIDTH = 400
const MIN_WIDTH = 50
const MAX_WIDTH = 640
const HANDLE_WIDTH = 10
const clamp = (width, maximum = MAX_WIDTH) => Math.round(Math.max(MIN_WIDTH, Math.min(maximum, width)))
// Reserve 300px for history, 20px for gaps, and 320px for the ECG workspace.
const maximumWidth = () => clamp(window.innerWidth - 640)

const initialWidth = () => {
	try {
		const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY))
		return Number.isFinite(saved) ? clamp(saved) : DEFAULT_WIDTH
	} catch {
		return DEFAULT_WIDTH
	}
}

const ResizableSidebar = ({ children, panelClassName = "" }) => {
	const [preferredWidth, setPreferredWidth] = useState(initialWidth)
	const [maximum, setMaximum] = useState(maximumWidth)
	const [dragging, setDragging] = useState(false)
	const drag = useRef(null)
	const panelId = useId()
	const width = Math.min(preferredWidth, maximum)

	useEffect(() => {
		const resize = () => setMaximum(maximumWidth())
		window.addEventListener("resize", resize)
		return () => window.removeEventListener("resize", resize)
	}, [])

	useEffect(() => {
		if (dragging) return
		try {
			window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferredWidth))
		} catch {
			// Resizing remains available when browser storage is disabled.
		}
	}, [preferredWidth, dragging])

	useEffect(() => {
		if (!dragging) return
		const { cursor, userSelect } = document.body.style
		document.body.style.cursor = "col-resize"
		document.body.style.userSelect = "none"
		return () => {
			document.body.style.cursor = cursor
			document.body.style.userSelect = userSelect
		}
	}, [dragging])

	const finishDrag = (event, cancel = false) => {
		if (!drag.current || (event.pointerId !== undefined && drag.current.pointerId !== event.pointerId)) return
		const { pointerId } = drag.current
		if (cancel) setPreferredWidth(drag.current.preferredWidth)
		drag.current = null
		setDragging(false)
		if (event.currentTarget.hasPointerCapture?.(pointerId)) {
			event.currentTarget.releasePointerCapture(pointerId)
		}
	}

	const onKeyDown = (event) => {
		if (event.key === "Escape" && drag.current) {
			event.preventDefault()
			finishDrag(event, true)
			return
		}
		const step = event.shiftKey ? 50 : 10
		const next = {
			ArrowLeft: width + step,
			ArrowRight: width - step,
			Home: MIN_WIDTH,
			End: maximum,
		}[event.key]
		if (next === undefined) return
		event.preventDefault()
		event.stopPropagation()
		setPreferredWidth(clamp(next, maximum))
	}

	return (
		<div className={styles.root} style={{ width: width + HANDLE_WIDTH }}>
			<div
				className={`${styles.handle} ${dragging ? styles.dragging : ""}`}
				role="separator"
				aria-label="Resize details sidebar"
				aria-orientation="vertical"
				aria-controls={panelId}
				aria-valuemin={MIN_WIDTH}
				aria-valuemax={maximum}
				aria-valuenow={width}
				aria-valuetext={`${width} pixels`}
				tabIndex={0}
				title="Drag or use arrow keys to resize; double-click to reset"
				onKeyDown={onKeyDown}
				onDoubleClick={() => setPreferredWidth(clamp(DEFAULT_WIDTH, maximum))}
				onPointerDown={(event) => {
					if (event.button !== 0 || event.isPrimary === false) return
					event.currentTarget.focus()
					event.currentTarget.setPointerCapture?.(event.pointerId)
					drag.current = { pointerId: event.pointerId, x: event.clientX, width, preferredWidth }
					setDragging(true)
				}}
				onPointerMove={(event) => {
					if (!drag.current || drag.current.pointerId !== event.pointerId) return
					setPreferredWidth(clamp(drag.current.width + drag.current.x - event.clientX, maximum))
				}}
				onPointerUp={finishDrag}
				onPointerCancel={(event) => finishDrag(event, true)}
				onLostPointerCapture={finishDrag}
			/>
			<aside id={panelId} aria-label="Recording details" className={`${styles.panel} ${panelClassName}`} style={{ width }}>
				{children}
			</aside>
		</div>
	)
}

export default ResizableSidebar
